import { Day } from "@/Common/Models/Domain/Day";

import { NotificationStatus, type NotificationTypeSummary } from "@/Modules/Models/Domain/ActivityCenter";

import type { ActivityCenterStore } from "@/Modules/ActivityCenter/Store";
import type { NotificationsListQuery } from "@/Modules/Models/Api/ActivityCenter";
import type { ACNotification } from "@/Modules/Models/Domain/ActivityCenter";
import NotificationTransformer, {
  type NotificationTransformerType,
} from "@/Modules/Transformers/Notification";

import ActivityCenterApiConnector from "@/Services/TherapyApi/ActivityCenter";

import { log } from "@/Utils/Debug";
import { getURLParam } from "@/Utils/URL";
import { getDateLabelFromUnixSeconds, unixSecondsToDateTime } from "@/Utils/Date";

class GetNotificationsActionHandler {
  constructor(
    private readonly activityCenterStore: ActivityCenterStore,
    private readonly activityCenterApiConnector: ActivityCenterApiConnector = new ActivityCenterApiConnector(),
    private readonly notificationTransformer: NotificationTransformerType = NotificationTransformer
  ) {}

  public async handle(notificationsListQuery: NotificationsListQuery, type = NotificationStatus.NEW) {
    try {
      this.activityCenterStore.isLoading = true;

      const res = await this.activityCenterApiConnector.getNotificationsList(notificationsListQuery);
      const notifications = res.body.map(this.notificationTransformer.toDomain);

      if (!notifications.length) return;

      let page: number | string | null = null;

      if (res?.meta?.next) {
        page = getURLParam(res.meta.next as string, "page");
        if (page) this.activityCenterStore.pagination[type] = parseInt(page);
      } else {
        this.activityCenterStore.pagination[type] = null;
      }

      if (type !== NotificationStatus.READ) {
        this.updateNewsAndActionRequiredNotifications(type, notifications, res.meta?.unreaded);
      } else {
        this.updateReadedNotifications(notifications);
      }
    } catch (e) {
      log("Error get notifications", e);
    } finally {
      this.activityCenterStore.isLoading = false;
    }
  }

  filterNotificationByType(
    accumutaledNotifications: NotificationTypeSummary,
    currentNotification: ACNotification,
    nextNotification: ACNotification | null
  ) {
    const dateLabel = this.getLabel(currentNotification.createdAt);
    const accumulatedNotifications = accumutaledNotifications[dateLabel]?.notifications || [];
    const title = accumutaledNotifications[dateLabel]?.title || dateLabel;

    const readedNotificationsPagination = this.activityCenterStore.pagination[NotificationStatus.READ];

    const finishedSection = nextNotification
      ? this.getLabel(nextNotification.createdAt) !== dateLabel
      : false;

    const newNotificationType = {
      notifications: [...accumulatedNotifications, currentNotification],
      title,
      finished: readedNotificationsPagination ? finishedSection : true,
    };

    return { dateLabel, newNotificationType };
  }

  getLabel(seconds: number): string {
    const dateLabel = getDateLabelFromUnixSeconds(seconds);

    if ([Day.TODAY, Day.YESTERDAY, Day.THIS_WEEK, Day.THIS_MONTH].includes(dateLabel)) {
      return dateLabel;
    } else {
      const date = unixSecondsToDateTime(seconds, { translated: false });
      const month = date.month;
      const year = date.year;

      return `${month}-${year}`;
    }
  }

  updateNewsAndActionRequiredNotifications(
    type: NotificationStatus,
    notifications: ACNotification[],
    unreadedNotifications: number | undefined
  ) {
    this.activityCenterStore.notificationsSummary[type] = {
      notifications: [
        ...(this.activityCenterStore.notificationsSummary[type]?.notifications || []),
        ...notifications,
      ],
      unreadedNotifications: unreadedNotifications,
      title: type,
    };
  }

  updateReadedNotifications(notifications: ACNotification[]) {
    const storedReadedNotifications: NotificationTypeSummary = {};

    Object.keys(this.activityCenterStore.notificationsSummary).forEach((key: string) => {
      if (![NotificationStatus.NEW, NotificationStatus.ACTION_REQUIRED].includes(key as NotificationStatus)) {
        storedReadedNotifications[key] = this.activityCenterStore.notificationsSummary[key]!;
      }
    });

    const filteredNotificationsByType = notifications.reduce(
      (
        accumulatedNotifications: NotificationTypeSummary,
        currentNotification: ACNotification,
        currentNotificationIndex: number,
        originalArray: ACNotification[]
      ) => {
        const { dateLabel, newNotificationType } = this.filterNotificationByType(
          accumulatedNotifications,
          currentNotification,
          originalArray[currentNotificationIndex + 1]!
        );

        return {
          ...accumulatedNotifications,
          [dateLabel]: { ...accumulatedNotifications[dateLabel], ...newNotificationType },
        };
      },
      storedReadedNotifications
    );

    this.activityCenterStore.notificationsSummary = {
      ...this.activityCenterStore.notificationsSummary,
      ...filteredNotificationsByType,
    };
  }
}

export default GetNotificationsActionHandler;
